import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";

interface ReversalResult {
  success: boolean;
  reversals: {
    ingredientId: string;
    ingredientName: string;
    quantity: number;
    previousStock: number;
    newStock: number;
  }[];
  errors: string[];
}

/**
 * Reverses stock deductions when an order is cancelled.
 * This function finds all stock movements related to the order and restores the stock.
 */
export async function reverseStockFromOrder(
  restaurantId: string,
  orderId: string
): Promise<ReversalResult> {
  const reversals: ReversalResult["reversals"] = [];
  const errors: string[] = [];

  try {
    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return { success: false, reversals: [], errors: ["Usuário não autenticado"] };
    }

    // Find all stock movements for this order
    const { data: movements, error: movementsError } = await supabase
      .from("stock_movements" as any)
      .select("*, ingredient:ingredients(id, name, current_stock)")
      .eq("order_id", orderId)
      .eq("type", "sale");

    if (movementsError) {
      logger.error("Error fetching order movements:", movementsError);
      return { success: false, reversals: [], errors: ["Erro ao buscar movimentações do pedido"] };
    }

    if (!movements || movements.length === 0) {
      // No stock movements to reverse
      return { success: true, reversals: [], errors: [] };
    }

    // Process each movement reversal
    for (const movement of movements as any[]) {
      const quantityToRestore = Math.abs(movement.quantity);
      const currentStock = movement.ingredient?.current_stock || 0;
      const newStock = currentStock + quantityToRestore;

      // Update ingredient stock
      const { error: updateError } = await supabase
        .from("ingredients" as any)
        .update({ current_stock: newStock })
        .eq("id", movement.ingredient_id);

      if (updateError) {
        logger.error(`Error restoring stock for ${movement.ingredient?.name}:`, updateError);
        errors.push(`Erro ao restaurar estoque de ${movement.ingredient?.name}`);
        continue;
      }

      // Create reversal movement record
      const { error: reversalMovementError } = await supabase
        .from("stock_movements" as any)
        .insert({
          restaurant_id: restaurantId,
          ingredient_id: movement.ingredient_id,
          type: "adjustment",
          quantity: quantityToRestore,
          previous_stock: currentStock,
          new_stock: newStock,
          unit_cost: movement.unit_cost || 0,
          total_cost: 0, // Reversal doesn't affect cost
          order_id: orderId,
          performed_by: user.id,
          reason: `Estorno - Cancelamento de pedido`,
        });

      if (reversalMovementError) {
        logger.error(`Error creating reversal movement:`, reversalMovementError);
        // Don't fail, just log
      }

      reversals.push({
        ingredientId: movement.ingredient_id,
        ingredientName: movement.ingredient?.name || "Desconhecido",
        quantity: quantityToRestore,
        previousStock: currentStock,
        newStock,
      });
    }

    logger.log(`Stock reversal completed: ${reversals.length} ingredients restored`);
    return { success: true, reversals, errors };

  } catch (error) {
    logger.error("Stock reversal error:", error);
    return { success: false, reversals: [], errors: ["Erro interno na reversão de estoque"] };
  }
}
